/**
 * Base scene class that all scenes extend
 * Provides common functionality for scene lifecycle and rendering
 */

import { Container } from 'pixi.js';
import type { TransitionType } from '@core/types';

export interface SceneContext {
  width: number;
  height: number;
  resolution: number;
}

export abstract class BaseScene extends Container {
  protected context: SceneContext;
  protected isActive: boolean = false;
  protected isPaused: boolean = false;

  constructor(context: SceneContext) {
    super();
    this.context = context;
  }

  /**
   * Called when scene is first created
   * Override to set up scene elements
   */
  abstract init(): Promise<void>;

  /**
   * Called when scene becomes active (after transition in)
   */
  onEnter(): void {
    this.isActive = true;
  }

  /**
   * Called when scene is about to become inactive (before transition out)
   */
  onExit(): void {
    this.isActive = false;
  }

  /**
   * Called every frame while scene is active
   */
  update(_delta: number): void {
    // Override in subclasses
  }

  /**
   * Called when window is resized
   */
  onResize(width: number, height: number): void {
    this.context.width = width;
    this.context.height = height;
    this.handleResize();
  }

  /**
   * Override to handle resize logic
   */
  protected handleResize(): void {
    // Override in subclasses
  }

  /**
   * Pause the scene
   */
  pause(): void {
    this.isPaused = true;
  }

  /**
   * Resume the scene
   */
  resume(): void {
    this.isPaused = false;
  }

  /**
   * Get preferred transition type for entering this scene
   */
  getEnterTransition(): TransitionType {
    return 'fade';
  }

  /**
   * Get preferred transition type for exiting this scene
   */
  getExitTransition(): TransitionType {
    return 'fade';
  }

  /**
   * Clean up resources when scene is destroyed
   */
  destroy(): void {
    super.destroy({ children: true });
  }
}
